import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LanguageProvider, useLanguage } from '@/contexts/LanguageContext';
import { Shield, Globe } from 'lucide-react';
import { Link } from 'react-router';

// Placeholder privacy policy content - in production, this would come from a backend API
const privacyPolicyContent = {
  en: `Privacy and Information Security Policy - Legalix.ai
Last updated: September 2026

Welcome to Legalix.ai (the "Company", the "System" or the "Services").
We recognise the extreme sensitivity of the information, data, legal documents, plans, calculations and trade secrets that our customers (lawyers, engineers, consultants, developers and organisations) upload to the System.

This policy sets out our strict contractual and technological commitment to absolute confidentiality, rigorous information security, and making no unauthorised use whatsoever of customer data.

1. Core principle: no model training on your data (Zero Model Training)

- Absolute prohibition on model training (No LLM Training): the information, documents, files (including CAD, DWG, PDF and Word files), data and text you upload to the System are never used to train, calibrate, improve or fine-tune artificial intelligence models (LLMs) - neither by the Company nor by third-party providers (such as Google, OpenAI, Anthropic and Groq).
- Isolated processing environments and zero retention outside the process: access to processing models is made through dedicated Enterprise interfaces and APIs that include a formal Zero-Data-Retention undertaking and a commitment not to use input/output data to improve the provider's services.

2. Exclusive ownership and commercial confidentiality

- Full ownership remains yours: every document, file, item of data, legal or planning information, insight or output produced for you in the System is your exclusive and complete property. The Company has no proprietary right, and shall have none, in that content.
- Confidentiality and privilege: the Company acknowledges that a substantial part of the information processed in the System is subject to attorney-client privilege, professional confidentiality or trade secrecy. The Company and its employees are bound by an absolute duty of confidentiality under law and by agreement.

3. No transfer, sale or sharing of information

- We do not sell, rent, trade or hand over information to third parties.
- Your information is not disclosed to any external party, competitor, advertiser or other third party, other than Enterprise-grade cloud infrastructure and security providers (such as secured cloud servers) acting solely as Data Processors under strict confidentiality and security agreements.

4. Information security and isolation architecture (Data Isolation & Security)

We apply advanced technological and organisational safeguards:

- End-to-end encryption:
  - In transit, using advanced secure communication protocols (TLS 1.3 / SSL).
  - At rest, for all files and databases (AES-256).
- Tenant & case isolation: every customer and every case/project is managed in an architecturally isolated environment that entirely prevents data leakage, cross-access or commingling of data between different users.
- Strict access control: access is limited to authorised personnel only (Role-Based Access Control), with access-log monitoring (Audit Logs).

5. Data retention, deletion and the right to be forgotten

- Full control over deletion: you may delete files, cases or processing history from the System at any time.
- Irreversible deletion: upon a deletion request or termination of the engagement, the information will be deleted from the Company's servers and backup repositories securely and permanently, subject to any law applicable to the retention of accounting records.

6. Your rights as a data subject

You are entitled to review the information collected about you, and to request its correction, the restriction of its processing, or its complete deletion.

7. Contact and Data Protection Officer

For any question, clarification or enquiry regarding privacy and information security, you may contact our Data Protection and Information Security Officer:

Email: privacy@legalix.ai / security@legalix.ai`,
  he: `מדיניות פרטיות ואבטחת מידע - Legalix.ai
עודכן לאחרונה: ספטמבר 2026

ברוכים הבאים ל-Legalix.ai (להלן: "החברה", "המערכת" או "השירותים").
אנו מודעים לרגישות העצומה של המידע, הנתונים, המסמכים המשפטיים, התוכניות, התחשיבים והסודות המסחריים המועלים למערכת על ידי לקוחותינו (עורכי דין, מהנדסים, יועצים, יזמים וארגונים).

מדיניות זו מפרטת את המחויבות הקשיחה, החוזית והטכנולוגית שלנו לשמירה על סודיות מוחלטת, אבטחת מידע קפדנית, ואי-עשיית כל שימוש בלתי מורשה בנתוני הלקוח.

1. עקרון הברזל: אימון מודלים ושימוש במידע (Zero Model Training)

- איסור מוחלט על אימון מודלים (No LLM Training): המידע, המסמכים, הקבצים (לרבות קובצי CAD, DWG, PDF, Word), הנתונים והטקסטים שאתה מעלה למערכת אינם משמשים לעולם לאימון, כיול, שיפור או כוונון (Fine-Tuning/Training) של מודלי בינה מלאכותית (LLMs) - לא של החברה ולא של ספקי צד שלישי (כגון Google, OpenAI, Anthropic ו-Groq).
- סביבות עיבוד מבודדות ואפס שמירה מחוץ לתהליך: הגישה למודלי העיבוד מתבצעת דרך ממשקי Enterprise ו-APIs ייעודיים הכוללים התחייבות רשמית לאי-שמירת נתונים (Zero-Data Retention) ואי-שימוש בנתוני קלט/פלט לצורכי שיפור שירותי הספק.

2. בעלות בלעדית וסודיות מסחרית

- הבעלות המלאה נשארת שלך: כל מסמך, קובץ, נתון, מידע משפטי/תכנוני, תובנה או תוצר המופק עבורך במערכת הינם רכושך הבלעדי והמלא. לחברה אין ולא תהיה כל זכות קניינית בתוכן זה.
- סודיות וחיסיון: החברה מכירה בכך שחלק ניכר מהמידע המעובד במערכת חוסה תחת חיסיון עורך דין-לקוח, סודיות מקצועית או סודות מסחריים. החברה ועובדיה מחויבים בחובת סודיות מוחלטת על פי דין והסכם.

3. איסור העברה, מכירה או שיתוף מידע

- איננו מוכרים, משכירים, סוחרים או מוסרים מידע לצדדים שלישיים.
- המידע שלך אינו נחשף לשום גורם חיצוני, מתחרה, מפרסם או צד ג' כלשהו, למעט ספקי תשתית מחשוב ענן ואבטחה ברמת Enterprise (כגון שרתי ענן מאובטחים) הפועלים כקבלני עיבוד בלבד (Data Processors) תחת הסכמי סודיות ואבטחה מחמירים.

4. ארכיטקטורת אבטחת מידע והפרדה (Data Isolation & Security)

אנו מיישמים אמצעי הגנה טכנולוגיים וארגוניים מתקדמים:

- הצפנה מלאה (Encryption):
  - הצפנה במעבר (In-Transit) באמצעות פרוטוקולי תקשורת מאובטחים מתקדמים (TLS 1.3 / SSL).
  - הצפנה במנוחה (At-Rest) של כלל הקבצים ומאגרי הנתונים (AES-256).
- בידוד לקוחות (Tenant & Case Isolation): כל לקוח וכל תיק/פרויקט מנוהלים בסביבה מבודדת ארכיטקטונית, המונעת לחלוטין זליגת מידע, גישה צולבת או עירוב נתונים בין משתמשים שונים.
- בקרת גישה קפדנית: גישה מוגבלת לפי מורשי גישה בלבד (Role-Based Access Control) וניטור יומני גישה (Audit Logs).

5. שמירת מידע, מחיקה וזכות להישכח

- שליטה מלאה במחיקת נתונים: באפשרותך בכל עת למחוק קבצים, תיקים או היסטוריית עיבודים מהמערכת.
- מחיקה בלתי הפיכה: עם הגשת בקשת מחיקה או סיום ההתקשרות, המידע יימחק משרתי החברה ומאגרי הגיבוי באופן מאובטח וסופי, בכפוף לכל דין החל על שמירת רשומות חשבונאיות.

6. הזכויות שלך כנושא המידע

הינך זכאי לעיין במידע שנאסף אודותיך, לדרוש את תיקונו, הגבלת עיבודו או מחיקתו המלאה.

7. יצירת קשר וממונה הגנת הפרטיות

לכל שאלה, הבהרה או פנייה בנושא פרטיות ואבטחת מידע, ניתן לפנות לממונה הגנת הפרטיות ואבטחת המידע שלנו:

דוא"ל: privacy@legalix.ai / security@legalix.ai`
};

const PrivacyPolicyPageContent = () => {
  const { language, toggleLanguage, t } = useLanguage();

  const content = language === 'he' ? privacyPolicyContent.he : privacyPolicyContent.en;

  return (
    <div className={`min-h-screen bg-background ${language === 'he' ? 'rtl' : 'ltr'}`}>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Language Toggle */}
        <div className="flex justify-end mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleLanguage}
            aria-label="Toggle language"
          >
            <Globe className="h-5 w-5" />
            <span className="ml-2 text-sm">{language === 'en' ? 'עב' : 'EN'}</span>
          </Button>
        </div>

        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex justify-center mb-4">
            <Shield className="h-16 w-16 text-chen-900" />
          </div>
          <h1 className="text-4xl font-bold text-chen-900 mb-4">{t('privacy.title')}</h1>
          <p className="text-muted-foreground">
            {t('privacy.lastUpdated')} {language === 'he' ? 'ספטמבר 2026' : 'September 2026'}
          </p>
        </div>

        {/* Privacy Policy Content */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl text-chen-900">
              {t('privacy.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-lg max-w-none">
            <div
              className={`whitespace-pre-wrap ${language === 'he' ? 'text-right font-serif' : 'text-left font-sans'}`}
              dir={language === 'he' ? 'rtl' : 'ltr'}
            >
              {content}
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="mt-8 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-chen-900">
              {language === 'he' ? 'צור קשר' : 'Contact Information'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className="text-muted-foreground">
                <strong>{language === 'he' ? 'אימייל:' : 'Email:'}</strong> privacy@legalix.ai
              </p>
              <p className="text-muted-foreground">
                <strong>{language === 'he' ? 'אתר:' : 'Website:'}</strong> https://legalix.ai
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            © {new Date().getFullYear()} Legalix.ai. {t('footer.copyright').replace('© 2025 Legalix.ai. All rights reserved.', '').trim()}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button variant="ghost" asChild>
              <Link to="/terms-of-service">{t('footer.terms')}</Link>
            </Button>
            <Button variant="ghost" asChild>
              <Link to="/">{t('nav.home')}</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PrivacyPolicyPage = () => {
  return (
    <LanguageProvider>
      <PrivacyPolicyPageContent />
    </LanguageProvider>
  );
};

export default PrivacyPolicyPage;